import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate } from "../lib/utils";
import { ArrowsLeftRight, DownloadSimple, Receipt, ArrowUp, ArrowDown, Calendar } from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";
import BottomSheet from "../components/BottomSheet";
import { DateRangeFilter } from "../components/DateRangeFilter";

export default function ClientTransactions() {
  const { t, lang } = useI18n();
  const [txs, setTxs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  useEffect(() => { api.get("/client/transactions").then(({ data }) => setTxs(data)); }, []);

  const filtered = useMemo(() => {
    if (!range?.from) return txs;
    const from = new Date(range.from); from.setHours(0, 0, 0, 0);
    const to = range.to ? new Date(range.to) : new Date(range.from);
    to.setHours(23, 59, 59, 999);
    return txs.filter((x) => {
      const d = new Date(x.created_at);
      return d >= from && d <= to;
    });
  }, [txs, range]);

  const isFiltered = !!range?.from;
  const credits = filtered.filter((x) => x.direction === "credit").reduce((s, x) => s + x.amount, 0);
  const debits  = filtered.filter((x) => x.direction === "debit").reduce((s, x) => s + x.amount, 0);

  const downloadProforma = async (tx) => {
    try {
      const res = await api.get(`/client/proforma-invoices/${tx.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proforma_n${tx.fic_proforma.number}_Teefarm.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      alert(t("err_download_doc"));
    }
  };

  return (
    <div className="space-y-6" data-testid="client-transactions">
      <PageHeader
        eyebrow={t("transactions")}
        title={t("tx_page_title")}
        subtitle={t("tx_page_subtitle")}
        chip="tf-chip-sky"
        icon={ArrowsLeftRight}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <div className="tf-card p-3 md:p-5 flex items-center gap-2.5 md:gap-4 min-w-0">
          <div className="tf-chip tf-chip-mint flex-shrink-0"><ArrowsLeftRight size={18} weight="fill" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] md:text-[11px] uppercase tracking-widest text-[var(--tf-muted)]">{t("tx_recharges")}</div>
            <div className="font-display font-black text-sm md:text-xl tf-numeric text-[var(--tf-success)] truncate">+{fmtEur(credits)}</div>
          </div>
        </div>
        <div className="tf-card p-3 md:p-5 flex items-center gap-2.5 md:gap-4 min-w-0">
          <div className="tf-chip tf-chip-peach flex-shrink-0"><ArrowsLeftRight size={18} weight="fill" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] md:text-[11px] uppercase tracking-widest text-[var(--tf-muted)]">{t("tx_debits")}</div>
            <div className="font-display font-black text-sm md:text-xl tf-numeric text-[var(--tf-error)] truncate">−{fmtEur(debits)}</div>
          </div>
        </div>
        <div className="tf-card p-3 md:p-5 flex items-center gap-2.5 md:gap-4 min-w-0 col-span-2 md:col-span-1">
          <div className="tf-chip tf-chip-lime flex-shrink-0"><ArrowsLeftRight size={18} weight="fill" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] md:text-[11px] uppercase tracking-widest text-[var(--tf-muted)]">{t("tx_movements")}</div>
            <div className="font-display font-black text-sm md:text-xl tf-numeric truncate">{filtered.length}</div>
          </div>
        </div>
      </div>

      <DateRangeFilter range={range} onChange={setRange} />

      <div className="tf-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto scroll-thin">
          <table className="tf-table">
            <thead><tr><th>{t("date")}</th><th>{t("type")}</th><th>{t("description")}</th><th className="text-right">{t("amount")}</th><th className="text-right">{t("tx_balance_after")}</th><th className="text-center">Proforma</th></tr></thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center text-[var(--tf-muted)] py-10" data-testid="tx-empty-state">
                  {isFiltered ? t("tx_empty_filtered") : t("empty")}
                </td></tr>
              )}
              {filtered.map((x) => (
                <tr key={x.id}>
                  <td className="text-xs">{fmtDate(x.created_at, lang)}</td>
                  <td className="text-xs uppercase tracking-wider">{x.type}</td>
                  <td className="text-xs">{x.description}</td>
                  <td className={`text-right tf-numeric font-bold ${x.direction === "credit" ? "text-[var(--tf-success)]" : "text-[var(--tf-error)]"}`}>
                    {x.direction === "credit" ? "+" : "−"}{fmtEur(x.amount)}
                  </td>
                  <td className="text-right tf-numeric">{fmtEur(x.balance_after)}</td>
                  <td className="text-center">
                    {x.fic_proforma?.pdf_url ? (
                      <button
                        onClick={() => downloadProforma(x)}
                        className="inline-flex items-center gap-1 text-xs text-[var(--tf-ink)] hover:underline font-semibold"
                        data-testid={`fic-pdf-${x.id}`}
                        title={t("tx_download_proforma", { n: x.fic_proforma.number })}
                      >
                        <Receipt size={13} weight="fill" />
                        <span className="tf-numeric">n.{x.fic_proforma.number}</span>
                        <DownloadSimple size={12} weight="bold" />
                      </button>
                    ) : (
                      <span className="text-[10px] text-[var(--tf-muted)]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden tf-mlist" data-testid="tx-mlist">
        {filtered.length === 0 && (
          <div className="tf-card p-8 text-center text-[var(--tf-muted)] text-sm" data-testid="tx-empty-state-mobile">
            <ArrowsLeftRight size={28} weight="duotone" className="mx-auto mb-2 opacity-60" />
            {isFiltered ? t("tx_empty_filtered") : t("empty")}
          </div>
        )}
        {filtered.map((x) => {
          const isCredit = x.direction === "credit";
          return (
            <button
              key={x.id}
              type="button"
              onClick={() => setSelected(x)}
              className="tf-mrow text-left"
              data-testid={`tx-card-${x.id}`}
            >
              <div
                className="tf-mrow-icon"
                style={{
                  background: isCredit ? "#D1F0E0" : "#FFE7D3",
                  color: isCredit ? "#1F6E4A" : "#A54B0F",
                }}
              >
                {isCredit ? <ArrowDown size={20} weight="bold" /> : <ArrowUp size={20} weight="bold" />}
              </div>
              <div className="tf-mrow-body">
                <div className="tf-mrow-title truncate">{x.description || x.type}</div>
                <div className="tf-mrow-sub flex items-center gap-1.5">
                  <Calendar size={11} weight="bold" />
                  {fmtDate(x.created_at, lang)}
                  {x.fic_proforma?.pdf_url && (
                    <>
                      <span className="opacity-40">·</span>
                      <Receipt size={11} weight="fill" />
                      <span className="tf-numeric">n.{x.fic_proforma.number}</span>
                    </>
                  )}
                </div>
              </div>
              <div className={`tf-mrow-value tf-numeric ${isCredit ? "text-[var(--tf-success)]" : "text-[var(--tf-error)]"}`}>
                {isCredit ? "+" : "−"}{fmtEur(x.amount)}
              </div>
            </button>
          );
        })}
      </div>

      {selected && (
        <BottomSheet
          open={!!selected}
          onClose={() => setSelected(null)}
          subtitle={selected.direction === "credit" ? t("tx_detail_recharge") : t("tx_detail_debit")}
          title={selected.description || selected.type}
          testId="tx-detail-sheet"
        >
          <div className="space-y-4">
            <div className="tf-card p-4 bg-[var(--tf-ink)] text-white">
              <div className="text-[10px] uppercase tracking-widest opacity-60">{t("amount")}</div>
              <div className={`font-display font-black text-3xl mt-1 tf-numeric ${selected.direction === "credit" ? "text-[var(--tf-primary)]" : "text-[#FFB4B4]"}`}>
                {selected.direction === "credit" ? "+" : "−"}{fmtEur(selected.amount)}
              </div>
              <div className="text-xs opacity-70 mt-2 tf-numeric">{t("tx_balance_after")}: {fmtEur(selected.balance_after)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="tf-card p-3">
                <div className="tf-label">{t("date")}</div>
                <div className="font-display font-bold text-sm mt-1">{fmtDate(selected.created_at, lang)}</div>
              </div>
              <div className="tf-card p-3">
                <div className="tf-label">{t("type")}</div>
                <div className="font-display font-bold text-sm mt-1 uppercase">{selected.type}</div>
              </div>
            </div>
            {selected.fic_proforma?.pdf_url && (
              <button
                onClick={() => downloadProforma(selected)}
                className="tf-btn tf-btn-primary w-full"
                data-testid="sheet-download-proforma"
              >
                <DownloadSimple size={16} weight="bold" /> {t("tx_download_proforma", { n: selected.fic_proforma.number })}
              </button>
            )}
          </div>
        </BottomSheet>
      )}
    </div>
  );
}
